import { FolderProviderState } from "../../contexts/screen-design-context/provider-partitions/folder-provider";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import type { IFolderBase } from "./folder-helper";

/** The folder families the project saves: `screenFolders`, `actionFolders` and `roleFolders`. */
export type DesignerFolderType = "screen" | "action" | "role";

const VALID_FOLDER_TYPES: DesignerFolderType[] = ["screen", "action", "role"];
const SUBFOLDER_TYPES: DesignerFolderType[] = ["screen", "action", "role"];

/**
 * Folder families `IProject` declares but the project does not save: `workFlowFolders` and
 * `databaseTablesFolders` are in neither the save diff (`convertProjectInterfaceToProjectMeta`) nor
 * the backend project model, and the load does not read them back. A folder made in one looked
 * fine until the next reload, when it vanished with what it held — so every tool refuses them with
 * the reason and what to do instead, rather than reporting a success that will not last.
 */
type UnsavedFolderType = "workflow" | "databaseTable";

const UNSAVED_FOLDER_TYPE_REFUSALS: Record<UnsavedFolderType, string> = {
  workflow:
    "Workflow folders aren't saved with the project yet, so the designer does not offer them. Leave workflows unfoldered.",
  databaseTable:
    "Table folders aren't saved with the project yet, so the designer does not offer them. Organise tables by schema instead."
};

interface IFolderSuccess {
  success: true;
  message: string;
  folderId?: string;
}

interface IFolderError {
  error: string;
}

function getFolderProvider(sd: IScreenDesignContextState): FolderProviderState {
  return sd;
}

function isUnsavedFolderType(folderType: string): folderType is UnsavedFolderType {
  return Object.prototype.hasOwnProperty.call(UNSAVED_FOLDER_TYPE_REFUSALS, folderType);
}

/** The folder type, or the refusal for one the project does not save or does not know. */
function resolveFolderType(folderType: string): DesignerFolderType | IFolderError {
  if (isUnsavedFolderType(folderType)) return { error: UNSAVED_FOLDER_TYPE_REFUSALS[folderType] };
  return VALID_FOLDER_TYPES.includes(folderType as DesignerFolderType)
    ? (folderType as DesignerFolderType)
    : buildInvalidFolderTypeError(folderType);
}

function isFolderError(value: DesignerFolderType | IFolderError): value is IFolderError {
  return typeof value !== "string";
}

/** The sub-folder type, or the refusal — a family the project does not save is refused as such. */
function resolveSubfolderType(folderType: string): DesignerFolderType | IFolderError {
  if (isUnsavedFolderType(folderType)) return { error: UNSAVED_FOLDER_TYPE_REFUSALS[folderType] };
  return SUBFOLDER_TYPES.includes(folderType as DesignerFolderType)
    ? (folderType as DesignerFolderType)
    : buildInvalidSubfolderTypeError(folderType);
}

function buildInvalidFolderTypeError(folderType: string): IFolderError {
  return { error: `Invalid folderType "${folderType}". Must be one of: ${VALID_FOLDER_TYPES.join(", ")}` };
}

function buildInvalidSubfolderTypeError(folderType: string): IFolderError {
  return {
    error: `Sub-folders are not supported for "${folderType}". Only supported for: ${SUBFOLDER_TYPES.join(", ")}`
  };
}

function getFoldersForType(sd: IScreenDesignContextState, folderType: DesignerFolderType): IFolderBase[] {
  const project = (sd as any).project;
  if (!project) return [];
  switch (folderType) {
    case "screen":
      return project.screenFolders || [];
    case "action":
      return project.actionFolders || [];
    case "role":
      return project.roleFolders || [];
    default:
      return [];
  }
}

function findFolderById(folders: IFolderBase[], folderId: string): IFolderBase | undefined {
  return folders.find((f) => f.id === folderId);
}

function findSubfolderById(
  folders: IFolderBase[],
  parentFolderId: string,
  subFolderId: string
): IFolderBase | undefined {
  const parent = folders.find((f) => f.id === parentFolderId);
  if (!parent || !parent.children) return undefined;
  return parent.children.find((c) => c.id === subFolderId);
}

export function createDesignerFolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; folderName?: string; dataSourceType?: string }
): IFolderSuccess | IFolderError {
  const { folderType, folderName, dataSourceType } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveFolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!folderName) return { error: "folderName is required" };
  if (resolvedType === "action" && !dataSourceType) {
    return { error: `dataSourceType is required for "${resolvedType}" folders (e.g. "ManagedDb", "RestApi")` };
  }

  const sd = getValues(sdInstance);
  const folderProvider = getFolderProvider(sd);

  // Check for duplicate name before creating
  const existingFolders = getFoldersForType(sd, resolvedType);
  if (existingFolders.some((f) => f.name === folderName)) {
    return { error: `A ${resolvedType} folder named "${folderName}" already exists` };
  }

  switch (resolvedType) {
    case "screen":
      folderProvider.addScreenFolder(folderName);
      break;
    case "action":
      folderProvider.addActionFolder(folderName, dataSourceType!);
      break;
    case "role":
      folderProvider.addRoleFolder?.(folderName);
      break;
  }

  // Look up the newly created folder by name to get its ID
  const updatedFolders = getFoldersForType(sd, resolvedType);
  const newFolder = updatedFolders.find((f) => f.name === folderName);

  return {
    success: true,
    message: `Created ${resolvedType} folder "${folderName}"`,
    folderId: newFolder?.id
  };
}

export function renameDesignerFolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; folderId?: string; folderName?: string }
): IFolderSuccess | IFolderError {
  const { folderType, folderId, folderName } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveFolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!folderId) return { error: "folderId is required" };
  if (!folderName) return { error: "folderName is required" };

  const sd = getValues(sdInstance);
  const folders = getFoldersForType(sd, resolvedType);
  const existing = findFolderById(folders, folderId);
  if (!existing) {
    return {
      error: `No ${resolvedType} folder found with id "${folderId}". Use list_folders to get valid folder IDs.`
    };
  }

  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.editScreenFolder(folderId, folderName);
      break;
    case "action":
      folderProvider.editActionFolder(folderId, folderName);
      break;
    case "role":
      folderProvider.editRoleFolder?.(folderId, folderName);
      break;
  }

  return { success: true, message: `Renamed ${resolvedType} folder "${existing.name}" to "${folderName}"` };
}

export function deleteDesignerFolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; folderId?: string }
): IFolderSuccess | IFolderError {
  const { folderType, folderId } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveFolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!folderId) return { error: "folderId is required" };

  const sd = getValues(sdInstance);
  const folders = getFoldersForType(sd, resolvedType);
  const existing = findFolderById(folders, folderId);
  if (!existing) {
    return {
      error: `No ${resolvedType} folder found with id "${folderId}". Use list_folders to get valid folder IDs.`
    };
  }

  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.deleteScreenFolder(folderId);
      break;
    case "action":
      folderProvider.deleteActionFolder(folderId);
      break;
    case "role":
      folderProvider.deleteRoleFolder?.(folderId);
      break;
  }

  return {
    success: true,
    message: `Deleted ${resolvedType} folder "${existing.name}" (${folderId}). Items inside are now unfoldered.`
  };
}

export function addDesignerItemToFolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; folderId?: string; itemId?: string }
): IFolderSuccess | IFolderError {
  const { folderType, folderId, itemId } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveFolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!folderId) return { error: "folderId is required" };
  if (!itemId) return { error: "itemId is required" };

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.addScreenToFolder(folderId, itemId);
      break;
    case "action":
      folderProvider.addActionToFolder(folderId, itemId);
      break;
    case "role":
      folderProvider.addRoleToFolder?.(folderId, itemId);
      break;
  }

  return { success: true, message: `Added item "${itemId}" to ${resolvedType} folder "${folderId}"` };
}

export function removeDesignerItemFromFolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; folderId?: string; itemId?: string }
): IFolderSuccess | IFolderError {
  const { folderType, folderId, itemId } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveFolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!folderId) return { error: "folderId is required" };
  if (!itemId) return { error: "itemId is required" };

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.removeScreenFromFolder(folderId, itemId);
      break;
    case "action":
      folderProvider.removeActionFromFolder(folderId, itemId);
      break;
    case "role":
      folderProvider.removeRoleFromFolder?.(folderId, itemId);
      break;
  }

  return { success: true, message: `Removed item "${itemId}" from ${resolvedType} folder "${folderId}"` };
}

export function createDesignerSubfolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; parentFolderId?: string; folderName?: string }
): IFolderSuccess | IFolderError {
  const { folderType, parentFolderId, folderName } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveSubfolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!parentFolderId) return { error: "parentFolderId is required" };
  if (!folderName) return { error: "folderName is required" };

  const sd = getValues(sdInstance);
  const folders = getFoldersForType(sd, resolvedType);
  const parent = findFolderById(folders, parentFolderId);
  if (!parent) {
    return {
      error: `No ${resolvedType} parent folder found with id "${parentFolderId}". Use list_folders to get valid folder IDs.`
    };
  }

  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.addScreenFolderInFolder(parentFolderId, folderName);
      break;
    case "action":
      folderProvider.addActionFolderInFolder(parentFolderId, folderName);
      break;
    case "role":
      folderProvider.addRoleFolderInFolder?.(parentFolderId, folderName);
      break;
  }

  // Look up the newly created subfolder by name
  const updatedFolders = getFoldersForType(sd, resolvedType);
  const updatedParent = findFolderById(updatedFolders, parentFolderId);
  const newSubfolder = updatedParent?.children?.find((c) => c.name === folderName);

  return {
    success: true,
    message: `Created sub-folder "${folderName}" inside ${resolvedType} folder "${parent.name}"`,
    folderId: newSubfolder?.id
  };
}

export function renameDesignerSubfolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; parentFolderId?: string; subFolderId?: string; folderName?: string }
): IFolderSuccess | IFolderError {
  const { folderType, parentFolderId, subFolderId, folderName } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveSubfolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!parentFolderId) return { error: "parentFolderId is required" };
  if (!subFolderId) return { error: "subFolderId is required" };
  if (!folderName) return { error: "folderName is required" };

  const sd = getValues(sdInstance);
  const folders = getFoldersForType(sd, resolvedType);
  const subfolder = findSubfolderById(folders, parentFolderId, subFolderId);
  if (!subfolder) {
    return {
      error: `No ${resolvedType} sub-folder found with id "${subFolderId}" under parent "${parentFolderId}". Use list_folders to get valid IDs.`
    };
  }

  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.editScreenFolderInFolder(parentFolderId, subFolderId, folderName);
      break;
    case "action":
      folderProvider.editActionFolderInFolder(parentFolderId, subFolderId, folderName);
      break;
    case "role":
      folderProvider.editRoleFolderInFolder?.(parentFolderId, subFolderId, folderName);
      break;
  }

  return { success: true, message: `Renamed sub-folder "${subfolder.name}" to "${folderName}"` };
}

export function deleteDesignerSubfolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; parentFolderId?: string; subFolderId?: string }
): IFolderSuccess | IFolderError {
  const { folderType, parentFolderId, subFolderId } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveSubfolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!parentFolderId) return { error: "parentFolderId is required" };
  if (!subFolderId) return { error: "subFolderId is required" };

  const sd = getValues(sdInstance);
  const folders = getFoldersForType(sd, resolvedType);
  const subfolder = findSubfolderById(folders, parentFolderId, subFolderId);
  if (!subfolder) {
    return {
      error: `No ${resolvedType} sub-folder found with id "${subFolderId}" under parent "${parentFolderId}". Use list_folders to get valid IDs.`
    };
  }

  const folderProvider = getFolderProvider(sd);
  switch (resolvedType) {
    case "screen":
      folderProvider.deleteScreenFolderInFolder(parentFolderId, subFolderId);
      break;
    case "action":
      folderProvider.deleteActionFolderInFolder(parentFolderId, subFolderId);
      break;
    case "role":
      folderProvider.deleteRoleFolderInFolder?.(parentFolderId, subFolderId);
      break;
  }

  return {
    success: true,
    message: `Deleted sub-folder "${subfolder.name}" from ${resolvedType} folder "${parentFolderId}". Items are now unfoldered.`
  };
}

export function addDesignerItemToSubfolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; parentFolderId?: string; subFolderId?: string; itemId?: string }
): IFolderSuccess | IFolderError {
  const { folderType, parentFolderId, subFolderId, itemId } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveSubfolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!parentFolderId) return { error: "parentFolderId is required" };
  if (!subFolderId) return { error: "subFolderId is required" };
  if (!itemId) return { error: "itemId is required" };

  const folderProvider = getFolderProvider(getValues(sdInstance));
  switch (resolvedType) {
    case "screen":
      folderProvider.addScreenToFolderInFolder(parentFolderId, subFolderId, itemId);
      break;
    case "action":
      folderProvider.addActionToFolderInFolder(parentFolderId, subFolderId, itemId);
      break;
    case "role":
      folderProvider.addRoleToFolderInFolder?.(parentFolderId, subFolderId, itemId);
      break;
  }

  return { success: true, message: `Added item "${itemId}" to sub-folder "${subFolderId}"` };
}

export function removeDesignerItemFromSubfolder(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string; parentFolderId?: string; subFolderId?: string; itemId?: string }
): IFolderSuccess | IFolderError {
  const { folderType, parentFolderId, subFolderId, itemId } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveSubfolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;
  if (!parentFolderId) return { error: "parentFolderId is required" };
  if (!subFolderId) return { error: "subFolderId is required" };
  if (!itemId) return { error: "itemId is required" };

  const folderProvider = getFolderProvider(getValues(sdInstance));
  switch (resolvedType) {
    case "screen":
      folderProvider.removeScreenFromFolderInFolder(parentFolderId, subFolderId, itemId);
      break;
    case "action":
      folderProvider.removeActionFromFolderInFolder(parentFolderId, subFolderId, itemId);
      break;
    case "role":
      folderProvider.removeRoleFromFolderInFolder?.(parentFolderId, subFolderId, itemId);
      break;
  }

  return { success: true, message: `Removed item "${itemId}" from sub-folder "${subFolderId}"` };
}

export function listDesignerFolders(
  sdInstance: ScreenDesignInstance,
  input: { folderType?: string }
): { folders: Array<{ id: string; name: string; children?: Array<{ id: string; name: string }> }> } | IFolderError {
  const { folderType } = input;
  const resolvedType: DesignerFolderType | IFolderError = resolveFolderType(folderType || "");
  if (isFolderError(resolvedType)) return resolvedType;

  const sd = getValues(sdInstance);
  const folders = getFoldersForType(sd, resolvedType);

  return {
    folders: folders.map((f) => ({
      id: f.id,
      name: f.name,
      ...(f.children && f.children.length > 0 ? { children: f.children.map((c) => ({ id: c.id, name: c.name })) } : {})
    }))
  };
}
